import React from "react";
import { Heart, Star, Briefcase, Globe, Video, CalendarClock } from "lucide-react";
import IMAGES from "../../../../constant/images";

const CounselorCard = ({ counselor, onFavoriteToggle, onBookNow, onViewProfile }) => {
  const {
    imageURL,
    firstName,
    lastName,
    title = "Dr.",
    experience,
    rating,
    languages = [],
    degree = null,
    specializations = [],
    isFavorite = false,
    consultationCount = 0,
    nextAvailableSlot,
  } = counselor;

  const fullName = `${title} ${firstName} ${lastName}`;
  const defaultImage = IMAGES.user_filled;
  const hasDegree = degree && degree.length > 0;
  // Blue subtitle shows degrees when available, otherwise the specialisations
  const subtitle = hasDegree ? degree.join(", ") : specializations.join(", ");
  const formattedRating = rating ? Number(rating).toFixed(1) : null;

  return (
    <div className="h-full bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm hover:shadow-md transition-shadow p-3 flex flex-col gap-3">
      <div className="flex gap-3">
        {/* Profile Image + Rating */}
        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => onViewProfile && onViewProfile(counselor)}
            className="w-[76px] h-[76px] sm:w-[88px] sm:h-[88px] p-0 rounded-xl border-0 bg-[#F8FAFC] shadow-[0_2px_8px_rgba(15,23,42,0.12)] overflow-hidden cursor-pointer"
          >
            <img
              src={imageURL || defaultImage}
              alt={fullName}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />
          </button>
          {formattedRating && (
            <div className="w-full flex items-center justify-center gap-1 bg-[#FEF9E7] border-[1px] border-solid border-[#FDECC8] rounded-lg py-0.5">
              <Star size={14} className="fill-[#B7791F] text-[#B7791F]" />
              <span className="text-sm font-semibold text-[#92610E] font-poppins">
                {formattedRating}
              </span>
            </div>
          )}
        </div>

        {/* Counselor Details */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          {/* Name and Favorite */}
          <div className="flex items-start justify-between gap-2">
            <h3
              onClick={() => onViewProfile && onViewProfile(counselor)}
              className="text-sm sm:text-base font-semibold text-[#171717] font-poppins truncate mb-0 cursor-pointer hover:text-[#2563EB] transition-colors"
              title={fullName}
            >
              {fullName}
            </h3>
            <button
              className="flex items-center justify-center w-[28px] h-[28px] p-0 bg-[#F1F5F9] border-0 rounded-full hover:bg-[#E2E8F0] transition-colors flex-shrink-0"
              onClick={() => onFavoriteToggle && onFavoriteToggle(counselor)}
            >
              <Heart
                size={14}
                className={isFavorite ? "fill-red-500 text-red-500" : "text-[#64748B]"}
              />
            </button>
          </div>

          {/* Degree / Specialisation */}
          {subtitle && (
            <span className="text-xs sm:text-sm text-[#2B4FB8] font-poppins font-semibold line-clamp-1">
              {subtitle}
            </span>
          )}

          {/* Info Chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {experience && (
              <span className="flex items-center gap-1 bg-[#F8FAFC] border-[1px] border-solid border-[#EEF2F6] rounded-md px-2 py-0.5 text-xs text-[#64748B] font-poppins">
                <Briefcase size={12} />
                {experience}+ Years
              </span>
            )}
            {languages.length > 0 && (
              <span
                className="flex items-center gap-1 bg-[#F8FAFC] border-[1px] border-solid border-[#EEF2F6] rounded-md px-2 py-0.5 text-xs text-[#64748B] font-poppins max-w-full"
                title={languages.join(", ")}
              >
                <Globe size={12} className="flex-shrink-0" />
                <span className="truncate">{languages.join(", ")}</span>
              </span>
            )}
            <span className="flex items-center gap-1.5 bg-[#F8FAFC] border-[1px] border-solid border-[#EEF2F6] rounded-md px-2 py-0.5 text-xs text-[#64748B] font-poppins">
              <Video size={14} className="fill-[#2563EB] text-[#2563EB]" />
              <span className="text-[#171717] font-semibold">{consultationCount}+</span>
              Consultations
            </span>
          </div>

          {/* Specialisation chips (when degree occupies the subtitle) */}
          {hasDegree && specializations.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {specializations.map((spec, index) => (
                <span
                  key={index}
                  className="bg-[#EEF2FF] border-[1px] border-solid border-[#E0E7FF] rounded-md px-2 py-0.5 text-xs font-medium text-[#2B4FB8] font-poppins whitespace-nowrap"
                >
                  {spec}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Next Available Slot */}
      {nextAvailableSlot && (
        <div className="mt-auto flex items-center gap-1.5 bg-[#EFF6FF] rounded-lg px-2.5 py-1.5 text-xs font-poppins">
          <CalendarClock size={14} className="flex-shrink-0 text-[#2563EB]" />
          <span className="text-[#64748B]">Next available:</span>
          <span className="font-semibold truncate text-[#2563EB]">{nextAvailableSlot}</span>
        </div>
      )}

      {/* Action Button */}
      <button
        onClick={() => onBookNow && onBookNow(counselor)}
        className={`${nextAvailableSlot ? "" : "mt-auto "}w-full btn-gradient border-0 h-9 rounded-lg flex items-center justify-center`}
      >
        <span className="text-sm font-semibold text-white font-poppins">
          Book Appointment
        </span>
      </button>
    </div>
  );
};

export default CounselorCard;
